/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Input,
  Modal,
  Form,
  InputNumber,
  Button,
  message,
  Space,
  Typography,
  Tooltip,
} from "antd";
import {
  GiftOutlined,
  HistoryOutlined,
  InboxOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { apiClient, getStorageUrl } from "../../../shared/api/AllApi";
import { invalidateCatalog, useProductsCatalog } from "../../../hooks/useCatalogData";
import { parseLocalizedName } from "../../../utils/localization";
import { formatNumber } from "../../../utils/formatters";
import usePermissions from "../../../hooks/usePermissions";
import { ListPage, StatCard, T } from "../../../shared/ui/PageShell";
import useTablePagination from "../../../shared/ui/useTablePagination";
import { fixedStart } from "../../../i18";

const { TextArea } = Input;
const { Text } = Typography;

const productOnHand = (item) => {
  const raw = item?.on_hand ?? item?.load_remaining ?? item?.stock ?? 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
};

const Stock = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [allProducts, setAllProducts] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);

  const { i18n, t } = useTranslation();
  const { data: catalogProducts, loading, refresh } = useProductsCatalog();
  const { pagination } = useTablePagination();

  useEffect(() => {
    const formattedProducts = catalogProducts.map((item) => ({
      id: item.id,
      name: parseLocalizedName(item.name, i18n.language, "N/A"),
      image_url: item.image_url ? getStorageUrl(item.image_url) : null,
      price: item.price,
      sku: item.sku || "",
      warehouseStock: Number(item.stock) || 0,
      loadRemaining: Number(item.load_remaining) || 0,
      stock: productOnHand(item),
    }));
    setAllProducts(formattedProducts);
  }, [catalogProducts, i18n.language]);

  const filteredProducts = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return allProducts;
    return allProducts.filter(
      (item) =>
        String(item.sku).toLowerCase().includes(q) ||
        String(item.name).toLowerCase().includes(q)
    );
  }, [allProducts, searchText]);

  const inStockCount = useMemo(
    () => allProducts.filter((p) => Number(p.stock) > 0).length,
    [allProducts]
  );
  const outOfStockCount = allProducts.length - inStockCount;

  const addStock = async (id, quantity, reason) => {
    try {
      const response = await apiClient.post(`/products/${id}/add-stock`, {
        quantity,
        ...(reason && String(reason).trim() ? { reason: String(reason).trim() } : {}),
      });

      if (response.status === 200) {
        invalidateCatalog("products");
        message.success(t("stockAdjustSuccess", "Stock updated successfully"));
        await refresh();
        setSelectedProduct(null);
      }
    } catch (error) {
      message.error(t("stockAdjustError", "Failed to update stock"));
      console.error("Error adding stock:", error);
    }
  };

  const removeStock = async (id, quantity, reason) => {
    try {
      const response = await apiClient.post(`/products/${id}/remove-stock`, {
        quantity,
        ...(reason && String(reason).trim() ? { reason: String(reason).trim() } : {}),
      });

      if (response.status === 200) {
        invalidateCatalog("products");
        message.success(t("stockAdjustSuccess", "Stock updated successfully"));
        await refresh();
        setSelectedProduct(null);
      }
    } catch (error) {
      message.error(error?.response?.data?.error || t("stockAdjustError", "Failed to update stock"));
      console.error("Error removing stock:", error);
    }
  };

  const ModalContent = () => {
    const [inputValue, setInputValue] = useState(0);
    const [reason, setReason] = useState("");

    useEffect(() => {
      setInputValue(0);
      setReason("");
    }, [selectedProduct?.id]);

    if (!selectedProduct) return null;

    return (
      <Modal
        open={!!selectedProduct}
        onCancel={() => setSelectedProduct(null)}
        footer={null}
        width={440}
        title={t("stockAdjustTitle", "Stock Adjustment")}
      >
        <div style={{ textAlign: "center" }}>
          <Text strong style={{ fontSize: 16, color: T.text }}>
            {selectedProduct.name}
          </Text>
          <div style={{ marginTop: 12, marginBottom: 8 }}>
            <span className={`stock-pill ${selectedProduct.stock > 0 ? "stock-pill--ok" : "stock-pill--empty"}`}>
              {formatNumber(selectedProduct.stock)}
            </span>
          </div>
          <p style={{ marginBottom: 12, fontSize: 12, color: T.muted }}>
            {t("warehouseStock", "Warehouse")}: {formatNumber(selectedProduct.warehouseStock)}
            {" · "}
            {t("loadRemaining", "On loads")}: {formatNumber(selectedProduct.loadRemaining)}
          </p>
          <Form layout="vertical" style={{ textAlign: "left", marginTop: 12 }}>
            <Form.Item label={t("quantity", "Quantity")}>
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                value={inputValue}
                onChange={(value) => setInputValue(value || 0)}
              />
            </Form.Item>
            <Form.Item label={t("adjustmentReasonOptional", "Reason (optional)")}>
              <TextArea
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t("adjustmentReasonPlaceholder", "e.g. medicine, inventory count, disposal…")}
              />
            </Form.Item>
            {hasPermission("stock.add") ? (
              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    onClick={() => addStock(selectedProduct.id, inputValue, reason)}
                    disabled={inputValue <= 0}
                  >
                    + {t("stockIn", "Stock In")}
                  </Button>
                  <Button
                    type="primary"
                    danger
                    onClick={() => removeStock(selectedProduct.id, inputValue, reason)}
                    disabled={
                      inputValue <= 0 || selectedProduct.warehouseStock - inputValue < 0
                    }
                  >
                    − {t("stockOut", "Stock Out")}
                  </Button>
                </Space>
              </Form.Item>
            ) : (
              <Text type="secondary">{t("noPermission", "No access to this section")}</Text>
            )}
          </Form>
        </div>
      </Modal>
    );
  };

  const columns = [
    {
      title: t("actions", "Actions"),
      key: "actions",
      width: 200,
      fixed: fixedStart(i18n.language),
      render: (_, row) => (
        <Space size={6} wrap>
          <Tooltip title={t("stockMovesLink", "Stock Movements")}>
            <Button
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => navigate(`/products/${row.id}/history`)}
              style={{ borderRadius: 8 }}
            />
          </Tooltip>
          {hasPermission("stock.add") && (
            <Button size="small" type="primary" onClick={() => setSelectedProduct(row)} style={{ borderRadius: 8 }}>
              {t("stockAdjustButton", "Adjust")}
            </Button>
          )}
        </Space>
      ),
    },
    {
      title: t("image", "Image"),
      dataIndex: "image_url",
      key: "img",
      width: 72,
      render: (url) =>
        url ? (
          <img
            src={url}
            alt=""
            height={48}
            width={48}
            style={{ objectFit: "cover", borderRadius: 10, border: `1px solid ${T.border}` }}
          />
        ) : (
          <div
            style={{
              width: 48,
              height: 48,
              background: T.borderL,
              border: `1px solid ${T.border}`,
              borderRadius: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: T.muted,
            }}
          >
            <InboxOutlined />
          </div>
        ),
    },
    {
      title: t("name", "Name"),
      dataIndex: "name",
      key: "name",
      render: (name, row) => (
        <div className="catalog-product-cell">
          <Link to={`/products/${row.id}/history`} className="catalog-product-cell__name">
            {name}
          </Link>
          {row.sku ? <span className="catalog-product-cell__meta">SKU {row.sku}</span> : null}
        </div>
      ),
    },
    {
      title: t("stock", "Stock"),
      dataIndex: "stock",
      key: "stock",
      width: 120,
      sorter: (a, b) => a.stock - b.stock,
      render: (stock) => {
        const empty = !Number.isFinite(stock) || stock <= 0;
        return (
          <span className={`stock-pill ${empty ? "stock-pill--empty" : "stock-pill--ok"}`}>
            {formatNumber(stock)}
          </span>
        );
      },
    },
    {
      title: t("loadRemaining", "On loads"),
      dataIndex: "loadRemaining",
      key: "loadRemaining",
      width: 110,
      render: (v) => (
        <span style={{ fontVariantNumeric: "tabular-nums", color: T.sub, fontWeight: 600 }}>
          {formatNumber(v)}
        </span>
      ),
    },
  ];

  const stats = [
    <StatCard
      key="total"
      icon={<InboxOutlined style={{ color: T.primary }} />}
      label={t("products", "Products")}
      value={allProducts.length}
      iconBg="#dbeafe"
    />,
    <StatCard
      key="instock"
      icon={<InboxOutlined style={{ color: T.green }} />}
      label={t("inStock", "In stock")}
      value={inStockCount}
      iconBg="#dcfce7"
      valueColor={T.green}
    />,
    <StatCard
      key="empty"
      icon={<WarningOutlined style={{ color: T.red }} />}
      label={t("outOfStock", "Out of stock")}
      value={outOfStockCount}
      iconBg="#ffe4e6"
      valueColor={outOfStockCount > 0 ? T.red : T.text}
    />,
  ];

  const toolbar = (
    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
      <Input.Search
        placeholder={t("searchProductsPlaceholder", "Search by name or SKU...")}
        allowClear
        style={{ width: 300, maxWidth: "100%" }}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        onSearch={setSearchText}
      />
      <div style={{ flex: 1 }} />
      {hasPermission("products.index") && (
        <Button icon={<GiftOutlined />} onClick={() => navigate("/products")}>
          {t("products", "Products")}
        </Button>
      )}
    </div>
  );

  return (
    <>
      <ListPage
        breadcrumb={[t("sidebarSectionCatalog", "Catalog"), t("stock", "Stock")]}
        title={t("stock", "Stock")}
        subtitle={t("stockPageSubtitle", "All products; movements via sales, purchases, or manual adjustments.")}
        stats={stats}
        toolbar={toolbar}
      >
        <Table
          rowKey="id"
          loading={loading}
          dataSource={filteredProducts}
          columns={columns}
          pagination={{ ...pagination, showTotal: (total) => `${total}` }}
          scroll={{ x: 780 }}
        />
      </ListPage>
      <ModalContent />
    </>
  );
};

export default Stock;
